import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Spinner, Dropdown } from "react-bootstrap";
import { useLazyQuery, useMutation, useSubscription } from "@apollo/client";
import { useToasts } from "react-toast-notifications";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faComment } from "@fortawesome/free-solid-svg-icons";
import { COLOR_APP } from "../../constants";
import { SHOP_APP_V2 } from "../../graphql/shop/queryShop";
import { GET_CONVERSATIONS, GET_CONVERSATION_THREADS } from "../../graphql/conversation/query";
import { REPLY_TO_CONVERSATION, MARK_CONVERSATION_READ } from "../../graphql/conversation/mutation";
import { SUBSCRIPTION_MESSENGER_COMMENT } from "../../graphql/conversation/subscription";

// Short Lao relative time, e.g. "ດຽວນີ້", "5 ນາທີ", "3 ຊມ.", "2 ມື້" — falls back to a date once it's old.
const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "ດຽວນີ້";
    if (diffMin < 60) return `${diffMin} ນາທີ`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour} ຊມ.`;
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay < 7) return `${diffDay} ມື້`;
    return new Date(dateStr).toLocaleDateString("lo-LA", { day: "numeric", month: "short" });
};

function MessengerInbox() {
    const { addToast } = useToasts();
    const shopId = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
        } catch (e) {
            return undefined;
        }
    }, []);

    const [pages, setPages] = useState([]); // [{_id,name,token,status}]
    const [selectedPageId, setSelectedPageId] = useState(null);
    const [threads, setThreads] = useState([]); // [{psid,name,lastMessage,lastDirection,lastCreatedAt,unreadCount}]
    const [messages, setMessages] = useState([]); // full history of the selected thread
    const [selectedPsid, setSelectedPsid] = useState(null);
    const [draftMessage, setDraftMessage] = useState("");
    const messagesEndRef = useRef(null);

    const [getShop, { loading: loadingShop }] = useLazyQuery(SHOP_APP_V2, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            const activePages = (data?.shop?.pages || []).filter((p) => p?.status);
            setPages(activePages);
            if (!selectedPageId && activePages.length > 0) {
                setSelectedPageId(activePages[0]._id);
            }
        },
    });

    useEffect(() => {
        if (shopId) getShop({ variables: { where: { id: shopId } } });
    }, [shopId, getShop]);

    const [getThreads, { loading: loadingThreads }] = useLazyQuery(GET_CONVERSATION_THREADS, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            setThreads(data?.conversationThreads || []);
        },
    });

    useEffect(() => {
        setSelectedPsid(null);
        setMessages([]);
        if (selectedPageId) {
            getThreads({ variables: { pageId: selectedPageId } });
        }
    }, [selectedPageId]);

    const [getConversations, { loading: loadingConversations }] = useLazyQuery(GET_CONVERSATIONS, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            setMessages(data?.conversations || []);
        },
    });

    const [markConversationRead] = useMutation(MARK_CONVERSATION_READ);
    const [replyToConversation, { loading: sending }] = useMutation(REPLY_TO_CONVERSATION);

    const { data: subscriptionData } = useSubscription(SUBSCRIPTION_MESSENGER_COMMENT, {
        variables: { pageId: selectedPageId || "" },
        skip: !selectedPageId,
    });

    useEffect(() => {
        if (selectedPageId && selectedPsid) {
            getConversations({ variables: { where: { pageId: selectedPageId, psid: selectedPsid } } });
            markConversationRead({ variables: { psid: selectedPsid, pageId: selectedPageId } }).catch(() => {});
            setThreads((prev) => prev.map((t) => (t.psid === selectedPsid ? { ...t, unreadCount: 0 } : t)));
        }
    }, [selectedPsid]);

    useEffect(() => {
        const incoming = subscriptionData?.onMessengerCommentCreated;
        if (!incoming) return;

        // Refresh the thread list so it re-sorts / bumps unread count for the new activity
        if (selectedPageId) getThreads({ variables: { pageId: selectedPageId } });

        // If the open thread received it, append inline instead of waiting for a refetch
        if (incoming.psid === selectedPsid) {
            setMessages((prev) => (prev.some((m) => m.id === incoming.id) ? prev : [...prev, incoming]));
        }
    }, [subscriptionData]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ block: "end" });
        }
    }, [messages]);

    const selectedThread = threads.find((t) => t.psid === selectedPsid);
    const selectedPage = pages.find((p) => p._id === selectedPageId);

    const handleSend = async () => {
        if (!draftMessage.trim() || !selectedPsid || !selectedPageId) return;
        try {
            await replyToConversation({
                variables: {
                    psid: selectedPsid,
                    pageId: selectedPageId,
                    message: draftMessage.trim(),
                },
            });
            setDraftMessage("");
            getThreads({ variables: { pageId: selectedPageId } });
        } catch (error) {
            console.log("replyToConversation error:", error);
            addToast("ສົ່ງຂໍ້ຄວາມບໍ່ສຳເລັດ, ກະລຸນາລອງໃໝ່", { appearance: "error", autoDismiss: true });
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 80px)", padding: "10px 0" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <FontAwesomeIcon icon={faComment} style={{ color: COLOR_APP, fontSize: 20 }} />
                <span style={{ fontWeight: "bold", fontSize: 22 }}>ຂໍ້ຄວາມລູກຄ້າ</span>

                {pages.length > 1 && (
                    <Dropdown onSelect={(key) => setSelectedPageId(key)}>
                        <Dropdown.Toggle size="sm" variant="outline-secondary" style={{ fontSize: 15 }}>
                            {selectedPage?.name || "ເລືອກ Page"}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {pages.map((p) => (
                                <Dropdown.Item key={p._id} eventKey={p._id} style={{ fontSize: 15 }}>
                                    {p.name}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
            </div>

            {loadingShop ? (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 40 }}>
                    <Spinner animation="border" />
                </div>
            ) : pages.length === 0 ? (
                <div style={{ textAlign: "center", color: "#888", marginTop: 40, fontSize: 16 }}>
                    ຍັງບໍ່ໄດ້ເຊື່ອມ Facebook Page — ໄປທີ່ໜ້າຕັ້ງຄ່າເພື່ອເຊື່ອມ Page ກ່ອນ
                </div>
            ) : (
                <div
                    style={{
                        flex: 1,
                        display: "flex",
                        background: "#FFF",
                        borderRadius: 12,
                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                    }}
                >
                    {/* Thread list */}
                    <div style={{ width: 360, flexShrink: 0, borderRight: "1px solid #F5F5FA", overflow: "auto", padding: 10 }}>
                        <div style={{ color: COLOR_APP, fontSize: 14, fontWeight: "bold", padding: "6px 8px" }}>
                            {loadingThreads ? <Spinner size="sm" animation="border" /> : `${threads.length} ລາຍການ`}
                        </div>
                        {threads.map((thread) => (
                            <div
                                key={thread.psid}
                                onClick={() => setSelectedPsid(thread.psid)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 12,
                                    padding: 12,
                                    borderRadius: 10,
                                    marginBottom: 6,
                                    cursor: "pointer",
                                    background: selectedPsid === thread.psid ? "#EEF0FF" : "transparent",
                                }}
                            >
                                {thread.profilePic ? (
                                    <img
                                        src={thread.profilePic}
                                        alt=""
                                        style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: "50%",
                                            background: "#E8E8F0",
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 16, fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {thread.name || thread.psid}
                                        </span>
                                        <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                            <span style={{ fontSize: 12, color: "#aaa", whiteSpace: "nowrap" }}>
                                                {formatRelativeTime(thread.lastCreatedAt)}
                                            </span>
                                            {thread.unreadCount > 0 && (
                                                <span style={{ background: "#FF3B30", color: "#FFF", borderRadius: 10, fontSize: 12, padding: "1px 8px" }}>
                                                    {thread.unreadCount}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    <div style={{ fontSize: 14, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {thread.lastMessage}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Thread view */}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                        <div style={{ padding: "14px 18px", borderBottom: "1px solid #F5F5FA", display: "flex", alignItems: "center", gap: 12 }}>
                            {selectedThread?.profilePic && (
                                <img
                                    src={selectedThread.profilePic}
                                    alt=""
                                    style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover" }}
                                />
                            )}
                            <span style={{ fontSize: 18, fontWeight: "bold" }}>
                                {selectedThread?.name || selectedThread?.psid || "ແຊັດ"}
                            </span>
                        </div>
                        <div style={{ flex: 1, overflow: "auto", padding: 18 }}>
                            {loadingConversations ? (
                                <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                                    <Spinner size="sm" animation="border" />
                                </div>
                            ) : (
                                messages.map((msg) => (
                                    <div
                                        key={msg.id}
                                        style={{
                                            display: "flex",
                                            justifyContent: msg.direction === "outbound" ? "flex-end" : "flex-start",
                                            margin: "8px 0",
                                        }}
                                    >
                                        <div
                                            style={{
                                                maxWidth: "60%",
                                                padding: "10px 16px",
                                                borderRadius: 14,
                                                background: msg.direction === "outbound" ? COLOR_APP : "#f2f2f2",
                                                color: msg.direction === "outbound" ? "#FFF" : "#000",
                                                fontSize: 16,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {msg.message}
                                        </div>
                                    </div>
                                ))
                            )}
                            {!selectedPsid && (
                                <div style={{ textAlign: "center", color: "#888", marginTop: 20, fontSize: 16 }}>ຍັງບໍ່ມີຂໍ້ຄວາມ</div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>
                        <div style={{ display: "flex", gap: 10, padding: 14, borderTop: "1px solid #F5F5FA" }}>
                            <Form.Control
                                as="textarea"
                                rows={1}
                                placeholder="ພິມຂໍ້ຄວາມຕອບກັບ..."
                                value={draftMessage}
                                disabled={!selectedPsid}
                                style={{ fontSize: 16 }}
                                onChange={(e) => setDraftMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSend();
                                    }
                                }}
                            />
                            <div
                                onClick={handleSend}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 48,
                                    flexShrink: 0,
                                    borderRadius: 8,
                                    background: COLOR_APP,
                                    color: "#FFF",
                                    fontSize: 18,
                                    cursor: !selectedPsid || !draftMessage.trim() || sending ? "not-allowed" : "pointer",
                                    opacity: !selectedPsid || !draftMessage.trim() || sending ? 0.5 : 1,
                                }}
                            >
                                {sending ? <Spinner size="sm" animation="border" /> : <FontAwesomeIcon icon={faPaperPlane} />}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default MessengerInbox;
